import { createSelector } from 'reselect'
import type { ReduxState } from '@/utils/typeHelpers'
import { getIsFetchingOrNotStarted } from '@/reducers/fetch'
import { FETCH_BANKING_PROFILE_KEY } from './bankingSlice'
import { BankingApplicationStatus } from './banking.types'
import {
  CONNECT_BANK_PATHS,
  HEARD_BANKING_PLAID_INSTITUTION_ID,
} from './banking.constants'
import { selectActivePlaidFinancialAccounts } from '@/selectors/financeSelectors'
import { FETCH_FINANCIAL_ACCOUNT_KEY } from '@/actions/financialAccountActions'
import { getIsRequiredStepsOnboarding } from '@/selectors/user.selectors'

const selectBankingState = (state: ReduxState) => state.banking

export const selectBankingProfile = createSelector(
  selectBankingState,
  (banking) => banking.profile
)

export const selectBankingApplicationStatus = createSelector(
  selectBankingProfile,
  (profile) => profile?.applicationStatus ?? null
)

export enum BankingDisplayState {
  AwaitingApplication = 'AwaitingApplication',
  ApprovedNoAccounts = 'ApprovedNoAccounts',
  NoAccountsFallback = 'NoAccountsFallback',
  Normal = 'Normal',
}

// True when the user has at least one active Plaid-linked FinancialAccount
// (Heard banking or any external bank). Reads the actual account state rather
// than onboarding-step rows, which are not always assigned.
export const selectHasConnectedBankAccount = createSelector(
  selectActivePlaidFinancialAccounts,
  (accounts): boolean => accounts.length > 0
)

// True when one of the user's active Plaid-linked FinancialAccounts is at
// Heard's institution (Unit). Distinguishes "has Heard banking specifically"
// from the broader "has any bank" check above — drives the
// "ApprovedNoAccounts" CUBK variant where we ask the user to connect their
// approved Heard banking account.
export const selectHasConnectedHeardBank = createSelector(
  selectActivePlaidFinancialAccounts,
  (accounts): boolean =>
    accounts.some(
      (a) => a.plaidInstitutionId === HEARD_BANKING_PLAID_INSTITUTION_ID
    )
)

// Both fetch keys must settle before the gate makes a decision — otherwise
// the financial-accounts data is unknown and the gate would briefly render
// the wrong state on initial load.
export const selectBankingDataLoaded = (state: ReduxState): boolean =>
  !getIsFetchingOrNotStarted(state, FETCH_BANKING_PROFILE_KEY) &&
  !getIsFetchingOrNotStarted(state, FETCH_FINANCIAL_ACCOUNT_KEY)

export const selectBankingDisplayState = createSelector(
  selectBankingApplicationStatus,
  selectHasConnectedBankAccount,
  selectHasConnectedHeardBank,
  selectBankingDataLoaded,
  (status, hasAnyBank, hasHeardBank, loaded): BankingDisplayState => {
    if (!loaded) return BankingDisplayState.Normal

    if (
      !hasAnyBank &&
      (status === BankingApplicationStatus.Pending ||
        status === BankingApplicationStatus.AwaitingDocuments ||
        status === BankingApplicationStatus.PendingReview)
    ) {
      return BankingDisplayState.AwaitingApplication
    }

    if (status === BankingApplicationStatus.Approved && !hasHeardBank) {
      return BankingDisplayState.ApprovedNoAccounts
    }

    if (hasAnyBank) return BankingDisplayState.Normal

    return BankingDisplayState.NoAccountsFallback
  }
)

/** A customer who has finished onboarding connects from the dashboard, never by re-entering onboarding. */
export const selectConnectBankPath = createSelector(
  getIsRequiredStepsOnboarding,
  (isOnboarding) =>
    isOnboarding ? CONNECT_BANK_PATHS.onboarding : CONNECT_BANK_PATHS.dashboard
)
